import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { toast } from "react-toastify";
import { withEmailId } from '../utils/auth';

const ValidationJobContext = createContext();

export const useValidationJobs = () => {
  const context = useContext(ValidationJobContext);
  if (!context) {
    throw new Error('useValidationJobs must be used within ValidationJobProvider');
  }
  return context;
};

export const ValidationJobProvider = ({ children }) => {
  const activePollsRef = useRef({}); // { jobId: intervalId }
  const [liveJobMap, setLiveJobMap] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const historyRefreshCallbacksRef = useRef([]);
  const mountedRef = useRef(true);

  // Register callback for history refresh
  const registerHistoryRefresh = useCallback((callback) => {
    historyRefreshCallbacksRef.current.push(callback);
    return () => {
      historyRefreshCallbacksRef.current = historyRefreshCallbacksRef.current.filter(
        cb => cb !== callback
      );
    };
  }, []);

  // Trigger all registered history refresh callbacks
  const triggerHistoryRefresh = useCallback(() => {
    historyRefreshCallbacksRef.current.forEach(callback => {
      if (typeof callback === 'function') {
        callback();
      }
    });
  }, []);

  const stopPolling = useCallback((jobId) => {
    const intervalId = activePollsRef.current[jobId];

    if (intervalId) {
      clearInterval(intervalId);
      delete activePollsRef.current[jobId];
    }

    if (Object.keys(activePollsRef.current).length === 0) {
      setIsPolling(false);
    }
  }, []);

  const pollJobStatus = useCallback((jobId) => {
    if (activePollsRef.current[jobId]) return; // Already polling this job

    setIsPolling(true);

    const intervalId = setInterval(async () => {
      try {
        const res = await fetch(withEmailId(`/mcv/validation_job_status?job_id=${jobId}`));
        const job = await res.json();

        const livePos = job?.queue_info?.current_position;
        const parentId = job?.payload?.parent_id;
        const jobStatus = job?.status;
        // { percentage, completed_units, failed_units, total_units } once ValidationManager
        // has computed total_units for this document — see /mcv/validation_job_status.
        const progress = job?.progress ?? null;

        if (parentId) {
          setLiveJobMap((prev) => ({
            ...prev,
            [parentId]: {
              status: jobStatus,
              current_position: typeof livePos === "number" ? livePos : null,
              progress,
            },
          }));
        }

        if (job.status === "SUCCESS") {
          if (parentId) {
            setLiveJobMap((prev) => {
              const copy = { ...prev };
              delete copy[parentId];
              return copy;
            });
          }
          stopPolling(jobId);
          toast.success(job.result?.data || `Validation completed (${jobId.slice(0, 6)}...)`);
          triggerHistoryRefresh();
        } 
        else if (job.status === "FAILED") {
          if (parentId) {
            setLiveJobMap((prev) => {
              const copy = { ...prev };
              delete copy[parentId];
              return copy;
            });
          }
          stopPolling(jobId);
          toast.error(job.error || `Validation failed (${jobId.slice(0, 6)}...)`);
          triggerHistoryRefresh();
        } 
        else if (job.status === "NOT_FOUND") {
          stopPolling(jobId);
          toast.error(`Job not found (${jobId.slice(0, 6)}...). Please try again.`);
        }

      } catch (err) {
        console.error(`Polling error for job ${jobId}:`, err);
        stopPolling(jobId);
        toast.error(`Polling error for job ${jobId.slice(0, 6)}...`);
      }
    }, 5000);

    activePollsRef.current[jobId] = intervalId;
  }, [stopPolling, triggerHistoryRefresh]);

  const resumePendingJobs = useCallback(async () => {
    try {
      const res = await fetch(withEmailId("/mcv/validation_jobs/pending"));
      const data = await res.json();

      if (data.status === "SUCCESS" && Array.isArray(data.data)) {
        console.log("🔄 Pending jobs:", data.data);

        data.data.forEach((job) => {
          pollJobStatus(job.job_id);
        });

        if (data.data.length > 0) {
          setIsPolling(true);
        }
      }
    } catch (err) {
      console.error("Failed to resume pending jobs:", err);
    }
  }, [pollJobStatus]);

  // Resume pending jobs on mount
  useEffect(() => {
    mountedRef.current = true;
    resumePendingJobs();

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      Object.values(activePollsRef.current).forEach(clearInterval);
      activePollsRef.current = {};
    };
  }, [resumePendingJobs]);

  const value = {
    liveJobMap,
    isPolling,
    pollJobStatus,
    stopPolling,
    resumePendingJobs,
    registerHistoryRefresh,
  };

  return (
    <ValidationJobContext.Provider value={value}>
      {children}
    </ValidationJobContext.Provider>
  );
};